import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Building2, Loader2, Mail, Phone, Save, ShieldCheck, UserCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';

const buildForm = (user: any) => ({
  full_name: user?.full_name || user?.fullName || user?.name || '',
  phone: user?.phone || '',
  email: user?.email || '',
  position: user?.position || '',
  address: user?.address || '',
});

export function CorporateProfile() {
  const { user, updateProfile, logout } = useAuth();
  const [profileUser, setProfileUser] = useState<any>(user);
  const [form, setForm] = useState(buildForm(user));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);
        const response: any = await userService.getUser(user.id);
        const loaded = response?.data || response || user;
        if (cancelled) return;
        setProfileUser(loaded);
        setForm(buildForm(loaded));
      } catch (error) {
        console.error('Corporate profile load failed:', error);
        if (!cancelled) setForm(buildForm(user));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [user]);

  const saveProfile = async () => {
    if (!user?.id) return;
    try {
      setSaving(true);
      await userService.updateUser(user.id, {
        full_name: form.full_name,
        phone: form.phone,
        position: form.position,
        address: form.address,
      });
      updateProfile({ name: form.full_name, phone: form.phone, position: form.position, address: form.address });
      toast.success('Profil güncellendi');
    } catch (error: any) {
      console.error('Corporate profile save failed:', error);
      toast.error(error?.message || 'Profil güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Profil yükleniyor...</div>;
  }

  const initials = String(form.full_name || user?.name || 'K').split(' ').map((part) => part[0]).join('').slice(0, 2).toLocaleUpperCase('tr-TR');
  const isSubUser = Boolean((profileUser as any)?.external_transport_account_user_id || (user as any)?.externalTransportAccountUserId);

  return (
    <div className="min-h-[calc(100vh-7rem)] bg-slate-50 pb-28 text-slate-900 sm:p-6">
      <div className="mx-auto max-w-4xl space-y-5 p-3 sm:p-0">
        <section className="overflow-hidden rounded-3xl bg-slate-950 p-6 text-white shadow-xl">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-orange-500 text-2xl font-black shadow-lg shadow-orange-950/30">{initials}</div>
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-300">Kurumsal Profil</div>
              <h1 className="mt-2 truncate text-2xl font-black sm:text-3xl">{form.full_name || 'Kullanıcı'}</h1>
              <p className="mt-1 text-sm text-slate-300">{isSubUser ? 'Kurumsal alt kullanıcı hesabı' : 'Kurumsal nakliye firma hesabı'}</p>
            </div>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><Building2 className="h-5 w-5 text-orange-600" /><div className="mt-2 text-xs font-semibold text-slate-500">Hesap Tipi</div><div className="mt-1 font-bold">{isSubUser ? 'Alt Kullanıcı' : 'Ana Firma'}</div></div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><ShieldCheck className="h-5 w-5 text-emerald-600" /><div className="mt-2 text-xs font-semibold text-slate-500">Yetki</div><div className="mt-1 font-bold">Kurumsal Panel</div></div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><UserCircle className="h-5 w-5 text-blue-600" /><div className="mt-2 text-xs font-semibold text-slate-500">Durum</div><div className="mt-1 font-bold">Aktif</div></div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-5">
            <h2 className="text-lg font-black">İletişim ve Hesap Bilgileri</h2>
            <p className="mt-1 text-sm text-slate-500">Bu bilgiler kurumsal panel bildirimleri ve destek iletişimi için kullanılır.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Ad Soyad / Firma Yetkilisi" value={form.full_name} onChange={(value) => setForm((current) => ({ ...current, full_name: value }))} />
            <Field label="Telefon" value={form.phone} onChange={(value) => setForm((current) => ({ ...current, phone: value }))} icon={<Phone className="h-4 w-4" />} />
            <Field label="E-posta" value={form.email} disabled onChange={() => undefined} icon={<Mail className="h-4 w-4" />} />
            <Field label="Görev / Ünvan" value={form.position} onChange={(value) => setForm((current) => ({ ...current, position: value }))} />
            <div className="sm:col-span-2"><Field label="Adres" value={form.address} onChange={(value) => setForm((current) => ({ ...current, address: value }))} /></div>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => void logout()} className="rounded-2xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50">Çıkış Yap</button>
            <button type="button" onClick={() => void saveProfile()} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-orange-500 px-5 py-3 text-sm font-bold text-white hover:bg-orange-600 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, disabled, icon }: { label: string; value: string; onChange: (value: string) => void; disabled?: boolean; icon?: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <div className="relative">
        {icon ? <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">{icon}</span> : null}
        <input value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} className={`h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-orange-400 disabled:bg-slate-100 disabled:text-slate-500 ${icon ? 'pl-11' : ''}`} />
      </div>
    </label>
  );
}

export default CorporateProfile;
